import type { OrderItem, Claim } from '../graphql/query/getOrder';

/**
 * Display item that may represent a claimed quantity or eligible quantity
 */
export type DisplayItem = OrderItem & {
  /** Unique display ID (itemId for eligible, itemId-claimId for claimed) */
  displayId: string;
  /** Single claim for this display item (if it's a claimed quantity) */
  singleClaim?: Claim;
  /** Whether this display item represents a claimed quantity (not selectable) */
  isClaimedItem: boolean;
};

/**
 * Expands items with claims into separate display items.
 * Each claimed quantity becomes its own card, and remaining eligible quantity
 * becomes a separate selectable card.
 */
export const expandItemsWithClaims = (items: OrderItem[]): DisplayItem[] => {
  const displayItems: DisplayItem[] = [];

  for (const item of items) {
    const claims = item.claims ?? [];

    if (claims.length === 0) {
      displayItems.push({
        ...item,
        displayId: item.id,
        isClaimedItem: false,
      });
    } else {
      const orderedQty = item.quantity?.ordered ?? 0;
      for (const claim of claims) {
        // Safety: Ensure claim quantity doesn't exceed ordered quantity
        // This prevents negative remaining quantities from data sync issues
        const claimQty = Math.min(claim.quantity ?? 1, orderedQty);

        displayItems.push({
          ...item,
          displayId: `${item.id}-${claim.claimItemId}`,
          singleClaim: claim,
          isClaimedItem: true,
          quantity: {
            ...item.quantity,
            ordered: claimQty,
            remaining: 0,
          },
        });
      }

      const remainingQty = item.quantity?.remaining ?? 0;
      if (remainingQty > 0) {
        displayItems.push({
          ...item,
          displayId: `${item.id}-eligible`,
          isClaimedItem: false,
          claims: null,
          quantity: {
            ...item.quantity,
            remaining: remainingQty,
          },
        });
      }
    }
  }

  return displayItems;
};
